import {
    Component,
    DestroyRef,
    inject,
    OnDestroy,
    OnInit,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { debounceTime, map, Observable } from 'rxjs';
import { ConfirmationDialogService } from '@polk_county_public_schools/ngx-pcps-dialogs/confirmation';
import { MessageBoxDialogService } from '@polk_county_public_schools/ngx-pcps-dialogs/messagebox';
import { OptionBoxDialogService } from '@polk_county_public_schools/ngx-pcps-dialogs/optionbox';
import { AppCollege } from '../../classes/app-college';
import { AppEssay } from '../../classes/app-essay';
import { AppFamily } from '../../classes/app-family';
import { AppFinancial } from '../../classes/app-financial';
import { AppFinalize } from '../../classes/app-finalize';
import { AppSelection } from '../../classes/app-selection';
import { AppStudent } from '../../classes/app-student';
import { ScholarshipResponses } from '../../classes/scholarship-responses';
import { StudentApp } from '../../classes/student-application';
import { UpdateFocus } from '../../classes/update-focus';
import { AppService } from '../../services/app.service';
import { SettingsService } from '../../services/settings.service';
import { SnackBarService } from '@polk_county_public_schools/ngx-pcps-dialogs/snackbar';
import { UserInfo, UserService } from '../../services/user.service';
import { validateNumber } from '../../validators/number.validator';
import { validatePhone } from '../../validators/phone.validator';
import { validateZipCode } from '../../validators/zipcode.validator';
import { STATES } from '../../classes/states';
import { Setting } from '../../classes/setting';
import { UnsavedChanges, UnsavedChangesService } from '../../routes/routes.guard';
import { AuthService } from '../../services/auth.service';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { CommonModule } from '@angular/common';
import { MatTabsModule } from '@angular/material/tabs';
import { MatRadioModule } from '@angular/material/radio';
import { MatDividerModule } from '@angular/material/divider';
import { InstructionsComponent } from '../instructions/instructions.component';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ScreenSizeService } from '@polk_county_public_schools/ngx-pcps-dialogs/core';
import { ErrorService } from '../../services/error.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SignatureCollectorComponent } from '../utilities/signature-collector/signature-collector.component';
import { AppSignature } from '../../classes/app-signature';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
    selector: 'app-student-application',
    templateUrl: './student-application.component.html',
    styleUrls: ['./student-application.component.scss'],
    imports: [
        ReactiveFormsModule,
        MatCardModule,
        MatFormFieldModule,
        MatInputModule,
        CommonModule,
        MatTabsModule,
        MatRadioModule,
        MatSelectModule,
        MatDividerModule,
        InstructionsComponent,
        MatButtonModule,
        MatCheckboxModule,
        MatProgressSpinnerModule,
        SignatureCollectorComponent,
        MatIconModule,
        MatTooltipModule
    ],
    providers: [
        OptionBoxDialogService
    ]
})
export class StudentApplicationComponent
    implements OnInit, OnDestroy, UnsavedChanges {
    protected readonly destroyRef = inject(DestroyRef);
    protected readonly appSvc = inject(AppService);
    protected readonly authSvc = inject(AuthService);
    protected readonly confirmDlgSvc = inject(ConfirmationDialogService);
    protected readonly messageDlgSvc = inject(MessageBoxDialogService);
    protected readonly optionDlgSvc = inject(OptionBoxDialogService);
    protected readonly screenSizeSvc = inject(ScreenSizeService);
    protected readonly router = inject(Router);
    protected readonly settingSvc = inject(SettingsService);
    protected readonly snackBarSvc = inject(SnackBarService);
    protected readonly unsavedChangesSvc = inject(UnsavedChangesService);
    protected readonly userSvc = inject(UserService);
    protected readonly errorSvc = inject(ErrorService);
    allStates = STATES;
    changeTrackingActive = false;
    endDateDisplay: string;
    pastDeadline: boolean = false;
    application: StudentApp;
    loading: boolean = true;
    studentForm: FormGroup;
    financialForm: FormGroup;
    familyForm: FormGroup;
    collegeForm: FormGroup;
    essayForm: FormGroup;
    instructionsRead = false;
    selectionForm: FormGroup;
    settings: Setting[];
    scholarshipArray: FormArray;
    formSubmitted: boolean = false;
    prevPage: number | null = null;
    currentPage: number = 0;
    highestPage: number = 0;
    initialApplication = '';
    savedSelections: ScholarshipResponses[] = [];
    studentID: string;
    user: UserInfo | null = null;
    smallScreen: boolean;
    xsScreen: boolean;
    deadlineException: boolean = false;
    studentSignature = signal<string | null>(null);
    parentSignature = signal<string | null>(null);
    highlighted: boolean = false;

    ngOnInit() {
        this.user = this.userSvc.User;
        this.studentID = this.userSvc.User.StudentID;
        this.getSettings();
        this.screenSizeSvc.ScreenSizeSub
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((size) => {
                if (size == null) return;
                this.smallScreen = size.IsSm || size.IsXs;
                this.xsScreen = size.IsXs;
            });
    }

    ngOnDestroy(): void {
        this.takeDownTracking();
    }

    canDeactivate(liveCheck?: boolean): boolean | Observable<boolean> | Promise<boolean> {
        // Are there unsaved changes in the current form, or are we not actively change tracking?
        if (!this.changeTrackingActive || !this.hasChanges()) {
            // No. The user can deactivate at will.
            return true;
        }

        // If this is a live check (native from browser), forego the confirmaion dialog as the native browser will catch instead.
        if (liveCheck === true) {
            return false;
        }

        // Need to prompt.
        return this.confirmDlgSvc
            .Open({
                TitleText: 'Unsaved Changes Detected',
                MessageText:
                    `The page currently has unsaved changes that have not yet been submitted. If you leave ` +
                    `this page at this time, these unsaved changes <strong>will be lost</strong>. Do you wish to discard the changes ` +
                    `and leave the page?`,
                OKText: 'Yes - Discard Changes',
                CancelText: 'No - Stay On Page',
                maxWidth: '600px',
            })
            .pipe(
                map((result) => {
                    if (result) {
                        // The user agreed. Take down change tracking now so we won't get prompted again.
                        this.takeDownTracking();
                    }

                    return !!result;
                })
            );
    }

    getSettings() {
        this.settingSvc.SettingsSubject.pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe((settings) => {
            this.settings = settings;
            if (settings == null) return;
            let endDate = settings.find(
                (f) => f.Label === 'Scholarship app end date'
            ).Value;
            this.endDateDisplay = settings.find(
                (f) => f.Label === 'Scholarship app end date (display)'
            ).Value;
            this.pastDeadline = new Date() > new Date(endDate);
            let exceptions = settings.find(
                (f) => f.Label === 'Allow Emer Access To These Students'
            ).Value;
            this.deadlineException = exceptions.includes(this.studentID);

            this.getStudentApplication();
        });
    }

    focusUpdate() {
        let dialog = this.confirmDlgSvc.Open({
            disableClose: true,
            TitleText: 'Notice',
            MessageText:
                `Only perform this action if you have incorrect information <strong>and you have told your school counselor.</strong> ` +
                `Once the information has been corrected in Focus, you can use this tool to retrieve the updated information and save it to your application.`,
            OKText: 'Get Data',
            CancelText: 'Cancel',
            width: '80%',
        });

        dialog.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res) => {
            if (res === true) {
                let ids: string[] = [];
                ids.push(this.application.StudentInfo.StudentID);
                let dto: UpdateFocus = {
                    StudentIDs: ids,
                };

                this.appSvc
                    .GetFocusData(dto)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe({
                        next: (res) => {
                            this.snackBarSvc.success(
                                'Data Updated',
                                5000
                            );
                            this.getStudentApplication();
                        },
                        error: (error) => {
                            this.errorSvc.HandleServiceError(error);
                        }
                    });
            }
        });
    }

    getApplicationCompareJSON() {
        return JSON.stringify({
            StudentForm: this.studentForm.getRawValue(),
            FinancialForm: this.financialForm.getRawValue(),
            FamilyForm: this.familyForm.getRawValue(),
            CollegeForm: this.collegeForm.getRawValue(),
            EssayForm: this.essayForm.getRawValue(),
            SelectionForm: this.selectionForm.getRawValue(),
        });
    }

    getStudentApplication() {
        this.appSvc
            .GetStudentApplication(this.studentID, this.userSvc.User.Training)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    this.application = res;
                    this.highestPage = res.HighestPage;
                    this.savedSelections = res.EligibleScholarships;
                    this.instructionsRead = res.InstructionsRead;
                    this.studentSignature.set(res.StudentSignature || null);
                    this.parentSignature.set(res.ParentSignature || null);

                    //go to the instructions page if past deadline and they haven't completed their application
                    this.currentPage = this.pastDeadline && res.AppFinalized === false && this.deadlineException === false
                        ? 0
                        : res.HighestPage;

                    // If the app is finalized, no need to continue loading this module. Switch to the app details.
                    if (res.AppFinalized === true) {
                        this.router.navigate([
                            'summary',
                            res.AppID,
                        ]);
                        return;
                    }

                    if (this.currentPage === 1) {
                        this.openStudentDialog();
                    } else if (res.HighestPage === 6) {
                        this.openSelectionDialog();
                    }

                    this.createForms();

                    // Initiate change tracking for the page.
                    this.setupTracking();

                    if (res.InstructionsRead === false) {
                        // Move the user to the instructions "page", specifying they have to do the checkboxes.
                        this.setPageNumber(0);
                    }

                    this.loading = false;
                },
                error: (error) => {
                    this.errorSvc.HandleServiceError(error);
                }
            });
    }

    openStudentDialog() {
        let dialog = this.messageDlgSvc.Open({
            maxWidth: '500px',
            TitleText:
                'Congratulations ' + this.application.StudentInfo.FirstName,
            MessageText:
                `This is the first step of your application. ` +
                `Most of what you see on this page has been filled in for your from school records. ` +
                `<br><br>There are some things you can change on this form, and some things you can not. ` +
                `<br><br>If you see any errors in the things you can change, please change them. ` +
                `If you see any errors in the things you cannot change, please contact your school counselor. ` +
                `<br><br>Be sure to check the tabs labeled "Transcript" and "Current Courses"`,
        });

        dialog.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
            this.highlighted = true;

            setTimeout(() => {
                this.highlighted = false;
            }, 3000);
        });
    }

    openSelectionDialog() {
        let dialog = this.messageDlgSvc.Open({
            maxWidth: '500px',
            TitleText:
                'Congratulations ' + this.application.StudentInfo.FirstName,
            MessageText:
                `You <strong>may</strong> be eligible for ` +
                this.application.EligibleScholarships.length +
                ` scholarships. ` +
                `Please read each description carefully to know if you are a right fit. ` +
                `<br><br>Select the scholarships you want to apply for using the checkboxes next to the name. ` +
                `<br><br>Scholarships may have yes/no questions, short-answer questions, or essays. ` +
                `All questions/essays listed on a scholarship are required. ` +
                `Some yes/no questions require a yes response to apply for the scholarship. ` +
                `<br><br>If an essay is required, read the description to know what to write about. `
        });

        dialog.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(res => {
            this.highlighted = true;

            setTimeout(() => {
                this.highlighted = false;
            }, 3000);
        });
    }

    handleInstructionsAction(route: string) {
        if (!route) return;

        // If we're going to the application page, then we need to mark instructions as read if they are not already.
        if (route === 'student-application' && !this.instructionsRead) {
            this.appSvc
                .ReadInstructions({
                    StudentID: this.userSvc.User.StudentID,
                })
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe({
                    next: async (res) => {
                        // We've marked the instructions as read. Set that and the InstructionsRead fields now.
                        this.application.InstructionsRead = true;
                        this.instructionsRead = true;
                        this.highestPage = 1;
                        this.setPageNumber(1);
                        await new Promise((r) => setTimeout(r, 500));
                        this.openStudentDialog();
                    },
                    error: (err) => {
                        this.snackBarSvc.error(err.error, 10000);
                    },
                });
        } else {
            // Route instead to the provided route.
            if (route === 'student-application') {
                // Go back to the last page we were on.
                this.setPageNumber(this.prevPage ?? this.highestPage);
            } else {
                this.router.navigate([route]);
            }
        }
    }

    hasChanges() {
        const current = this.getApplicationCompareJSON();

        return current !== this.initialApplication;
    }

    createForms() {
        this.studentForm = new FormGroup({
            Address: new FormControl(this.application.StudentInfo.Address, Validators.required,),
            Apt: new FormControl(this.application.StudentInfo.Apt),
            City: new FormControl(this.application.StudentInfo.City, Validators.required),
            State: new FormControl(this.application.StudentInfo.State, Validators.required),
            Zip: new FormControl(
                this.application.StudentInfo.Zip,
                [Validators.required, validateZipCode],
            ),
            Phone: new FormControl(
                this.application.StudentInfo.Phone,
                [
                    Validators.required,
                    validatePhone,
                ]
            ),
            PersonalEmail: new FormControl(
                this.application.StudentInfo.PersonalEmail,
                [
                    Validators.required,
                    Validators.email
                ]
            ),
            SchoolEmail: new FormControl(
                this.application.StudentInfo.SchoolEmail,
                [
                    Validators.required,
                    Validators.email
                ]
            )
        });

        this.financialForm = new FormGroup({
            Ffaa: new FormControl(this.application.FinancialInfo.CompletedFFAA, Validators.required),
            HavePrepaid: new FormControl(
                this.application.FinancialInfo.HavePrepaid,
                Validators.required,
            ),
            Sai: new FormControl(this.application.FinancialInfo.Sai),
            NeedBased: new FormControl(this.application.FinancialInfo.NeedBased, Validators.required)
        });

        this.familyForm = new FormGroup({
            MotherName: new FormControl(this.application.FamilyInfo.MotherName),
            MotherEmployer: new FormControl(this.application.FamilyInfo.MotherEmployer),
            FatherName: new FormControl(this.application.FamilyInfo.FatherName),
            FatherEmployer: new FormControl(this.application.FamilyInfo.FatherEmployer),
            HouseholdSize: new FormControl(this.application.FamilyInfo.HouseholdSize, validateNumber),
            FamilyInCollegeNumber: new FormControl(this.application.FamilyInfo.FamilyInCollegeNumber, validateNumber)
        });

        this.collegeForm = new FormGroup({
            SelectedInstitution: new FormControl(this.application.CollegeInfo.SelectedInstitution, Validators.required),
            Accepted: new FormControl(
                this.application.CollegeInfo.Accepted,
                Validators.required,
            ),
            IntendedMajor: new FormControl(this.application.CollegeInfo.IntendedMajor, Validators.required),
            WhyMajor: new FormControl(this.application.CollegeInfo.WhyMajor, Validators.required),
            EmploymentHistory: new FormControl(this.application.CollegeInfo.EmploymentHistory, Validators.required),
            VolunteerDescription: new FormControl(this.application.CollegeInfo.VolunteerDescription, Validators.required),
            OtherAwards: new FormControl(this.application.CollegeInfo.OtherAwards, Validators.required)
        });

        this.essayForm = new FormGroup({
            Essay: new FormControl(this.application.Essay, Validators.required),
            EssayAI: new FormControl(this.application.EssayAI, Validators.required),
            AiHow: new FormControl(this.application.AiHow, Validators.required)
        });

        this.selectionForm = new FormGroup({
            Scholarships: new FormArray([]),
        });

        this.scholarshipArray = this.selectionForm.get(
            'Scholarships'
        ) as FormArray;

        for (let sch of this.application.EligibleScholarships) {
            let fGroup = this.createScholarshipSelection(sch);
            this.scholarshipArray.push(fGroup);
        }

        this.setRequiredValidation();

        //if select scholarship, make fields required
        this.scholarshipArray.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(1000))
            .subscribe((val) => {
                this.setRequiredValidation();
            });

        //AI question enable/disable
        if (this.essayForm.get('EssayAI').value === true) {
            this.essayForm.get('AiHow').enable();
        }
        else {
            this.essayForm.get('AiHow').disable();
        }

        this.essayForm.get('EssayAI').valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((val) => {
            if (val === true) {
                this.essayForm.get('AiHow').enable();
            }
            else {
                this.essayForm.get('AiHow').disable();
            }
        });
    }

    setRequiredValidation() {
        for (let s of this.scholarshipArray.controls) {
            if (s.value.AdditionalQuestion != null) {
                if (s.value.AqHaveText === true) {
                    //if select scholarship, add required validations to general selectionForm of all scholarships
                    if (s.value.Selected === true) {
                        if (
                            !s
                                .get('AdditionalQuestionTextResponse')
                                .hasValidator(Validators.required)
                        ) {
                            s.get('AdditionalQuestionTextResponse').addValidators(
                                Validators.required
                            );
                            s.get(
                                'AdditionalQuestionTextResponse'
                            ).updateValueAndValidity();
                        }
                    }
                    //if deselect, take out validations so they're not required to submit selectionForm
                    else {
                        if (
                            s
                                .get('AdditionalQuestionTextResponse')
                                .hasValidator(Validators.required)
                        ) {
                            s.get('AdditionalQuestionTextResponse').removeValidators(
                                Validators.required
                            );
                            s.get(
                                'AdditionalQuestionTextResponse'
                            ).updateValueAndValidity();
                        }
                    }
                }
                if (s.value.AqHaveYesNo === true) {
                    //if scholarship is selected, add validator
                    if (s.value.Selected === true) {
                        //if yes is required and validator is not already on, add Validators.requiredTrue
                        if (s.value.AqYesRequired === true &&
                            !s.get('AdditionalQuestionYesNoResponse').hasValidator(Validators.requiredTrue)
                        ) {
                            s.get('AdditionalQuestionYesNoResponse').addValidators(Validators.requiredTrue);
                            s.get('AdditionalQuestionYesNoResponse').updateValueAndValidity();
                        }
                        //if yes isn't required and validator is not already on, add Validators.required
                        else if (s.value.AqYesRequired !== true &&
                            !s.get('AdditionalQuestionYesNoResponse').hasValidator(Validators.required)
                        ) {
                            s.get('AdditionalQuestionYesNoResponse').addValidators(Validators.required);
                            s.get('AdditionalQuestionYesNoResponse').updateValueAndValidity();
                        }
                    }
                    //else, remove validator
                    else {
                        if (s.value.AqYesRequired === true &&
                            s.get('AdditionalQuestionYesNoResponse').hasValidator(Validators.requiredTrue)
                        ) {
                            s.get('AdditionalQuestionYesNoResponse').removeValidators(Validators.requiredTrue);
                            s.get('AdditionalQuestionYesNoResponse').updateValueAndValidity();
                        }
                        else if (s.value.AqYesRequired !== true &&
                            s.get('AdditionalQuestionYesNoResponse').hasValidator(Validators.required)
                        ) {
                            s.get('AdditionalQuestionYesNoResponse').removeValidators(Validators.required);
                            s.get('AdditionalQuestionYesNoResponse').updateValueAndValidity();
                        }
                    }
                }
            }

            if (s.value.EmployeeNameLabel != null) {
                if (s.value.Selected === true) {
                    if (
                        !s
                            .get('EmployeeNameResponse')
                            .hasValidator(Validators.required)
                    ) {
                        s.get('EmployeeNameResponse').addValidators(
                            Validators.required
                        );
                        s.get('EmployeeNameResponse').updateValueAndValidity();
                    }
                } else {
                    if (
                        s
                            .get('EmployeeNameResponse')
                            .hasValidator(Validators.required)
                    ) {
                        s.get('EmployeeNameResponse').removeValidators(
                            Validators.required
                        );
                        s.get('EmployeeNameResponse').updateValueAndValidity();
                    }
                }
            }

            if (s.value.EmployeeLocationLabel != null) {
                if (s.value.Selected === true) {
                    if (
                        !s
                            .get('EmployeeLocationResponse')
                            .hasValidator(Validators.required)
                    ) {
                        s.get('EmployeeLocationResponse').addValidators(
                            Validators.required
                        );
                        s.get(
                            'EmployeeLocationResponse'
                        ).updateValueAndValidity();
                    }
                } else {
                    if (
                        s
                            .get('EmployeeLocationResponse')
                            .hasValidator(Validators.required)
                    ) {
                        s.get('EmployeeLocationResponse').removeValidators(
                            Validators.required
                        );
                        s.get(
                            'EmployeeLocationResponse'
                        ).updateValueAndValidity();
                    }
                }
            }

            if (s.value.EmployeeSSNLabel != null) {
                if (s.value.Selected === true) {
                    if (
                        !s
                            .get('EmployeeSSNResponse')
                            .hasValidator(Validators.required)
                    ) {
                        s.get('EmployeeSSNResponse').addValidators(
                            Validators.required
                        );
                        s.get('EmployeeSSNResponse').updateValueAndValidity();
                    }
                } else {
                    if (
                        s
                            .get('EmployeeSSNResponse')
                            .hasValidator(Validators.required)
                    ) {
                        s.get('EmployeeSSNResponse').removeValidators(
                            Validators.required
                        );
                        s.get('EmployeeSSNResponse').updateValueAndValidity();
                    }
                }
            }

            if (s.value.StoreLocationNumberLabel != null) {
                if (s.value.Selected === true) {
                    if (
                        !s
                            .get('StoreLocationNumberResponse')
                            .hasValidator(Validators.required)
                    ) {
                        s.get('StoreLocationNumberResponse').addValidators(
                            Validators.required
                        );
                        s.get(
                            'StoreLocationNumberResponse'
                        ).updateValueAndValidity();
                    }
                } else {
                    if (
                        s
                            .get('StoreLocationNumberResponse')
                            .hasValidator(Validators.required)
                    ) {
                        s.get('StoreLocationNumberResponse').removeValidators(
                            Validators.required
                        );
                        s.get(
                            'StoreLocationNumberResponse'
                        ).updateValueAndValidity();
                    }
                }
            }

            if (s.value.AccountNameLabel != null) {
                if (s.value.Selected === true) {
                    if (
                        !s
                            .get('AccountNameResponse')
                            .hasValidator(Validators.required)
                    ) {
                        s.get('AccountNameResponse').addValidators(
                            Validators.required
                        );
                        s.get('AccountNameResponse').updateValueAndValidity();
                    }
                } else {
                    if (
                        s
                            .get('AccountNameResponse')
                            .hasValidator(Validators.required)
                    ) {
                        s.get('AccountNameResponse').removeValidators(
                            Validators.required
                        );
                        s.get('AccountNameResponse').updateValueAndValidity();
                    }
                }
            }

            if (s.value.AccountNumberLabel != null) {
                if (s.value.Selected === true) {
                    if (
                        !s
                            .get('AccountNumberResponse')
                            .hasValidator(Validators.required)
                    ) {
                        s.get('AccountNumberResponse').addValidators(
                            Validators.required
                        );
                        s.get('AccountNumberResponse').updateValueAndValidity();
                    }
                } else {
                    if (
                        s
                            .get('AccountNumberResponse')
                            .hasValidator(Validators.required)
                    ) {
                        s.get('AccountNumberResponse').removeValidators(
                            Validators.required
                        );
                        s.get('AccountNumberResponse').updateValueAndValidity();
                    }
                }
            }

            if (s.value.ParentNameLabel != null) {
                if (s.value.Selected === true) {
                    if (
                        !s
                            .get('ParentNameResponse')
                            .hasValidator(Validators.required)
                    ) {
                        s.get('ParentNameResponse').addValidators(
                            Validators.required
                        );
                        s.get('ParentNameResponse').updateValueAndValidity();
                    }
                } else {
                    if (
                        s
                            .get('ParentNameResponse')
                            .hasValidator(Validators.required)
                    ) {
                        s.get('ParentNameResponse').removeValidators(
                            Validators.required
                        );
                        s.get('ParentNameResponse').updateValueAndValidity();
                    }
                }
            }

            if (s.value.ParentSSNLabel != null) {
                if (s.value.Selected === true) {
                    if (
                        !s
                            .get('ParentSSNResponse')
                            .hasValidator(Validators.required)
                    ) {
                        s.get('ParentSSNResponse').addValidators(
                            Validators.required
                        );
                        s.get('ParentSSNResponse').updateValueAndValidity();
                    }
                } else {
                    if (
                        s
                            .get('ParentSSNResponse')
                            .hasValidator(Validators.required)
                    ) {
                        s.get('ParentSSNResponse').removeValidators(
                            Validators.required
                        );
                        s.get('ParentSSNResponse').updateValueAndValidity();
                    }
                }
            }

            if (s.value.ParentOrganizationLabel != null) {
                if (s.value.Selected === true) {
                    if (
                        !s
                            .get('ParentOrganizationResponse')
                            .hasValidator(Validators.required)
                    ) {
                        s.get('ParentOrganizationResponse').addValidators(
                            Validators.required
                        );
                        s.get(
                            'ParentOrganizationResponse'
                        ).updateValueAndValidity();
                    }
                } else {
                    if (
                        s
                            .get('ParentOrganizationResponse')
                            .hasValidator(Validators.required)
                    ) {
                        s.get('ParentOrganizationResponse').removeValidators(
                            Validators.required
                        );
                        s.get(
                            'ParentOrganizationResponse'
                        ).updateValueAndValidity();
                    }
                }
            }

            if (s.value.ParentDepartmentLabel != null) {
                if (s.value.Selected === true) {
                    if (
                        !s
                            .get('ParentDepartmentResponse')
                            .hasValidator(Validators.required)
                    ) {
                        s.get('ParentDepartmentResponse').addValidators(
                            Validators.required
                        );
                        s.get(
                            'ParentDepartmentResponse'
                        ).updateValueAndValidity();
                    }
                } else {
                    if (
                        s
                            .get('ParentDepartmentResponse')
                            .hasValidator(Validators.required)
                    ) {
                        s.get('ParentDepartmentResponse').removeValidators(
                            Validators.required
                        );
                        s.get(
                            'ParentDepartmentResponse'
                        ).updateValueAndValidity();
                    }
                }
            }

            if (s.value.ScholarshipEssayLabel != null) {
                if (s.value.Selected === true) {
                    if (
                        !s
                            .get('ScholarshipEssayResponse')
                            .hasValidator(Validators.required)
                    ) {
                        s.get('ScholarshipEssayResponse').addValidators(
                            Validators.required
                        );
                        s.get(
                            'ScholarshipEssayResponse'
                        ).updateValueAndValidity();
                    }
                } else {
                    if (
                        s
                            .get('ScholarshipEssayResponse')
                            .hasValidator(Validators.required)
                    ) {
                        s.get('ScholarshipEssayResponse').removeValidators(
                            Validators.required
                        );
                        s.get(
                            'ScholarshipEssayResponse'
                        ).updateValueAndValidity();
                    }
                }
            }

            if (s.value.ScholarshipEssay2Label != null) {
                if (s.value.Selected === true) {
                    if (
                        !s
                            .get('ScholarshipEssay2Response')
                            .hasValidator(Validators.required)
                    ) {
                        s.get('ScholarshipEssay2Response').addValidators(
                            Validators.required
                        );
                        s.get(
                            'ScholarshipEssay2Response'
                        ).updateValueAndValidity();
                    }
                } else {
                    if (
                        s
                            .get('ScholarshipEssay2Response')
                            .hasValidator(Validators.required)
                    ) {
                        s.get('ScholarshipEssay2Response').removeValidators(
                            Validators.required
                        );
                        s.get(
                            'ScholarshipEssay2Response'
                        ).updateValueAndValidity();
                    }
                }
            }
        }
    }

    createScholarshipSelection(sch: ScholarshipResponses): FormGroup {
        let element = new FormGroup({
            Selected: new FormControl(sch.Selected),
            ScholarshipID: new FormControl(sch.ScholarshipID),
            ScholarshipName: new FormControl(sch.ScholarshipName),
            ScholarshipDescription: new FormControl(sch.ScholarshipDescription),
            AdditionalQuestion: new FormControl(sch.AdditionalQuestion),
            AqHaveYesNo: new FormControl(sch.AqHaveYesNo),
            AqYesRequired: new FormControl(sch.AqYesRequired),
            AqHaveText: new FormControl(sch.AqHaveText),
            AdditionalQuestionTextResponse: new FormControl(sch.AdditionalQuestionTextResponse),
            AdditionalQuestionYesNoResponse: new FormControl(sch.AdditionalQuestionYesNoResponse),
            EmployeeNameLabel: new FormControl(sch.EmployeeNameLabel),
            EmployeeNameResponse: new FormControl(sch.EmployeeNameResponse),
            EmployeeLocationLabel: new FormControl(sch.EmployeeLocationLabel),
            EmployeeLocationResponse: new FormControl(sch.EmployeeLocationResponse),
            EmployeeSSNLabel: new FormControl(sch.EmployeeSSNLabel),
            EmployeeSSNResponse: new FormControl(sch.EmployeeSSNResponse),
            StoreLocationNumberLabel: new FormControl(sch.StoreLocationNumberLabel),
            StoreLocationNumberResponse: new FormControl(sch.StoreLocationNumberResponse),
            AccountNameLabel: new FormControl(sch.AccountNameLabel),
            AccountNameResponse: new FormControl(sch.AccountNameResponse),
            AccountNumberLabel: new FormControl(sch.AccountNumberLabel),
            AccountNumberResponse: new FormControl(sch.AccountNumberResponse),
            ParentNameLabel: new FormControl(sch.ParentNameLabel),
            ParentNameResponse: new FormControl(sch.ParentNameResponse),
            ParentSSNLabel: new FormControl(sch.ParentSSNLabel),
            ParentSSNResponse: new FormControl(sch.ParentSSNResponse),
            ParentOrganizationLabel: new FormControl(sch.ParentOrganizationLabel),
            ParentOrganizationResponse: new FormControl(sch.ParentOrganizationResponse),
            ParentDepartmentLabel: new FormControl(sch.ParentDepartmentLabel),
            ParentDepartmentResponse: new FormControl(sch.ParentDepartmentResponse),
            ScholarshipEssayLabel: new FormControl(sch.ScholarshipEssayLabel),
            ScholarshipEssayResponse: new FormControl(sch.ScholarshipEssayResponse),
            ScholarshipEssay2Label: new FormControl(sch.ScholarshipEssay2Label),
            ScholarshipEssay2Response: new FormControl(sch.ScholarshipEssay2Response)
        });

        if (sch.AdditionalQuestion == null) {
            element.get('AdditionalQuestionTextResponse').disable();
            element.get('AdditionalQuestionYesNoResponse').disable();
        }

        if (sch.AqHaveText !== true) {
            element.get('AdditionalQuestionTextResponse').disable();
        }

        if (sch.AqHaveYesNo !== true) {
            element.get('AdditionalQuestionYesNoResponse').disable();
        }

        if (sch.EmployeeNameLabel == null) {
            element.get('EmployeeNameResponse').disable();
        }

        if (sch.EmployeeLocationLabel == null) {
            element.get('EmployeeLocationResponse').disable();
        }

        if (sch.EmployeeSSNLabel == null) {
            element.get('EmployeeSSNResponse').disable();
        }

        if (sch.StoreLocationNumberLabel == null) {
            element.get('StoreLocationNumberResponse').disable();
        }

        if (sch.AccountNameLabel == null) {
            element.get('AccountNameResponse').disable();
        }

        if (sch.AccountNumberLabel == null) {
            element.get('AccountNumberResponse').disable();
        }

        if (sch.ParentNameLabel == null) {
            element.get('ParentNameResponse').disable();
        }

        if (sch.ParentSSNLabel == null) {
            element.get('ParentSSNResponse').disable();
        }

        if (sch.ParentOrganizationLabel == null) {
            element.get('ParentOrganizationResponse').disable();
        }

        if (sch.ParentDepartmentLabel == null) {
            element.get('ParentDepartmentResponse').disable();
        }

        if (sch.ScholarshipEssayLabel == null) {
            element.get('ScholarshipEssayResponse').disable();
        }

        if (sch.ScholarshipEssay2Label == null) {
            element.get('ScholarshipEssay2Response').disable();
        }

        return element;
    }

    setPageNumber(page: number) {
        // Is this an eligible page to switch to?
        if (
            this.currentPage != page &&
            this.highestPage >= page
        ) {
            this.formSubmitted = false; //flag for form validation. Just one across all sections, so reset when change sections (pages)
            this.prevPage = this.currentPage;
            this.currentPage = page;
        }
    }

    setupTracking(): void {
        if (!this.changeTrackingActive) {
            this.unsavedChangesSvc.SetTracking(
                this.canDeactivate.bind(this),
                this.hasChanges.bind(this)
            );
            this.changeTrackingActive = true;
            this.initialApplication = this.getApplicationCompareJSON();
        }
    }

    takeDownTracking(): void {
        this.unsavedChangesSvc.ClearTracking();
        this.changeTrackingActive = false;
    }

    submitStudent(form: FormGroup) {
        if (form.invalid) {
            this.formSubmitted = true;
            return;
        }

        this.formSubmitted = false;

        let dto: AppStudent = {
            AppID: this.application.AppID,
            Address: form.value.Address,
            Apt: form.value.Apt,
            City: form.value.City,
            State: form.value.State,
            Zip: form.value.Zip,
            Phone: form.value.Phone,
            PersonalEmail: form.value.PersonalEmail,
            SchoolEmail: form.value.SchoolEmail
        };

        this.appSvc
            .SaveAppStudent(dto)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    this.snackBarSvc.success('Save Successful', 3000);
                    this.currentPage = 2;

                    if (this.highestPage < 2) {
                        this.highestPage = 2;
                    }

                    // Update the student portion of the initial application, for change tracking.
                    this.updateInitialForChangeTracking(1);

                    this.authSvc.resetIdleTimer();
                },
                error: (error) => {
                    this.errorSvc.HandleServiceError(error);
                }
            });
    }

    submitFinancial(form: FormGroup) {
        if (form.invalid) {
            this.formSubmitted = true;
            this.financialForm.markAllAsTouched();
            return;
        }

        this.formSubmitted = false;

        let dto: AppFinancial = {
            AppID: this.application.AppID,
            Ffaa: form.value.Ffaa,
            HavePrepaid: form.value.HavePrepaid,
            Sai: form.value.Sai,
            NeedBased: form.value.NeedBased
        };

        this.appSvc
            .SaveAppFinancial(dto)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    this.snackBarSvc.success('Save Successful', 3000);
                    this.currentPage = 3;

                    if (this.highestPage < 3) {
                        this.highestPage = 3;
                    }

                    // Update the Financial portion of the initial application, for change tracking.
                    this.updateInitialForChangeTracking(2);

                    this.authSvc.resetIdleTimer();
                },
                error: (error) => {
                    this.errorSvc.HandleServiceError(error);
                }
            });
    }

    submitFamily(form: FormGroup) {
        if (form.invalid) {
            this.formSubmitted = true;
            return;
        }

        this.formSubmitted = false;

        let dto: AppFamily = {
            AppID: this.application.AppID,
            MotherName: form.value.MotherName,
            MotherEmployer: form.value.MotherEmployer,
            FatherName: form.value.FatherName,
            FatherEmployer: form.value.FatherEmployer,
            HouseholdSize: form.value.HouseholdSize,
            FamilyInCollegeNumber: form.value.FamilyInCollegeNumber,
        };

        this.appSvc
            .SaveAppFamily(dto)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    this.snackBarSvc.success('Save Successful', 3000);
                    this.currentPage = 4;

                    if (this.highestPage < 4) {
                        this.highestPage = 4;
                    }

                    // Update the family portion of the initial application, for change tracking.
                    this.updateInitialForChangeTracking(3);

                    this.authSvc.resetIdleTimer();
                },
                error: (error) => {
                    this.errorSvc.HandleServiceError(error);
                }
            });
    }

    submitCollege(form: FormGroup) {
        if (form.invalid) {
            this.formSubmitted = true;
            this.collegeForm.markAllAsTouched();
            return;
        }

        this.formSubmitted = false;

        let dto: AppCollege = {
            AppID: this.application.AppID,
            SelectedInstitution: form.value.SelectedInstitution,
            Accepted: form.value.Accepted,
            IntendedMajor: form.value.IntendedMajor,
            WhyMajor: form.value.WhyMajor,
            EmploymentHistory: form.value.EmploymentHistory,
            VolunteerDescription: form.value.VolunteerDescription,
            OtherAwards: form.value.OtherAwards,
        };

        this.appSvc
            .SaveAppCollege(dto)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    this.snackBarSvc.success('Save Successful', 3000);
                    this.currentPage = 5;

                    if (this.highestPage < 5) {
                        this.highestPage = 5;
                    }

                    // Update the college portion of the initial application, for change tracking.
                    this.updateInitialForChangeTracking(4);

                    this.authSvc.resetIdleTimer();
                },
                error: (error) => {
                    this.errorSvc.HandleServiceError(error);
                }
            });
    }

    submitEssay(form: FormGroup) {
        if (form.invalid) {
            this.formSubmitted = true;
            return;
        }

        this.formSubmitted = false;

        let dto: AppEssay = {
            AppID: this.application.AppID,
            Essay: form.value.Essay,
            EssayAI: form.value.EssayAI,
            AiHow: form.value.AiHow
        };

        this.appSvc
            .SaveAppEssay(dto)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    this.snackBarSvc.success('Save Successful', 3000);
                    this.currentPage = 6;

                    if (this.highestPage < 6) {
                        this.highestPage = 6;
                    }

                    // Update the essay portion of the initial application, for change tracking.
                    this.updateInitialForChangeTracking(5);

                    this.authSvc.resetIdleTimer();

                    this.openSelectionDialog();
                },
                error: (error) => {
                    this.errorSvc.HandleServiceError(error);
                }
            });
    }

    submitSelection(form: FormGroup) {
        if (form.invalid) {
            this.formSubmitted = true;
            this.snackBarSvc.error(
                'There are unanswered question(s) in one or more of your selections',
                5000
            );
            return;
        }

        this.formSubmitted = false;
        let scholarships: ScholarshipResponses[] = [];

        for (let s of form.value.Scholarships) {
            let sch: ScholarshipResponses = new ScholarshipResponses();
            sch.Selected = s.Selected;
            sch.ScholarshipID = s.ScholarshipID;
            sch.ScholarshipName = s.ScholarshipName;
            sch.AdditionalQuestionTextResponse = s.AdditionalQuestionTextResponse;
            sch.AdditionalQuestionYesNoResponse = s.AdditionalQuestionYesNoResponse;
            sch.AqHaveText = s.AqHaveText;
            sch.AqHaveYesNo = s.AqHaveYesNo;
            sch.AqYesRequired = s.AqYesRequired;
            sch.EmployeeNameResponse = s.EmployeeNameResponse;
            sch.EmployeeLocationResponse = s.EmployeeLocationResponse;
            sch.EmployeeSSNResponse = s.EmployeeSSNResponse;
            sch.StoreLocationNumberResponse = s.StoreLocationNumberResponse;
            sch.AccountNameResponse = s.AccountNameResponse;
            sch.AccountNumberResponse = s.AccountNumberResponse;
            sch.ParentNameResponse = s.ParentNameResponse;
            sch.ParentSSNResponse = s.ParentSSNResponse;
            sch.ParentOrganizationResponse = s.ParentOrganizationResponse;
            sch.ParentDepartmentResponse = s.ParentDepartmentResponse;
            sch.ScholarshipEssayResponse = s.ScholarshipEssayResponse;
            sch.ScholarshipEssay2Response = s.ScholarshipEssay2Response;
            scholarships.push(sch);
        }

        let dto: AppSelection = {
            AppID: this.application.AppID,
            Scholarships: scholarships,
        };

        let unselectedResponses = this.anyUnselectedResponses(dto.Scholarships);

        if (unselectedResponses.length > 0) {
            let message =
                `The following scholarships have questions that have been answered, but the scholarship has not been selected. ` +
                `Did you forget to select these scholarships? ` +
                `This site will only save responses of selected scholarships. ` +
                `<ul>`;

            for (let s of unselectedResponses) {
                message = message.concat('<li>' + s + '</li>');
            }

            message = message.concat('</ul>');

            let dialog = this.optionDlgSvc.Open({
                disableClose: true,
                width: '70%',
                TitleText: 'Did You Forget?',
                MessageText: message,
                Options: [
                    {
                        DisplayText: `Go Back To Application`,
                        Color: 'primary',
                        Response: null,
                    },
                    {
                        DisplayText: 'Ignore These Scholarships And Save Selections',
                        Color: 'primary',
                        Response: 'save',
                    },
                ],
            });

            dialog
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((response) => {
                    if (response === 'save') {
                        this.postSelections(dto);
                    }
                });
        } else {
            this.postSelections(dto);
        }
    }

    selectionsEdited(form: ScholarshipResponses[]): boolean {
        for (let sch of form) {
            let lastSave = this.savedSelections.find(
                (f) => f.ScholarshipID === sch.ScholarshipID
            );

            //if unselect one or make new selection
            if (sch.Selected != lastSave.Selected) {
                return true;
            }
            //only care about edits to selected scholarships
            else if (sch.Selected === true) {
                if (
                    sch.AdditionalQuestionTextResponse !=
                    lastSave.AdditionalQuestionTextResponse
                ) {
                    return true;
                }
                if (sch.EmployeeNameResponse != lastSave.EmployeeNameResponse) {
                    return true;
                }
                if (
                    sch.EmployeeLocationResponse !=
                    lastSave.EmployeeLocationResponse
                ) {
                    return true;
                }
                if (sch.EmployeeSSNResponse != lastSave.EmployeeSSNResponse) {
                    return true;
                }
                if (
                    sch.StoreLocationNumberResponse !=
                    lastSave.StoreLocationNumberResponse
                ) {
                    return true;
                }
                if (sch.AccountNameResponse != lastSave.AccountNameResponse) {
                    return true;
                }
                if (
                    sch.AccountNumberResponse != lastSave.AccountNumberResponse
                ) {
                    return true;
                }
                if (sch.ParentNameResponse != lastSave.ParentNameResponse) {
                    return true;
                }
                if (sch.ParentSSNResponse != lastSave.ParentSSNResponse) {
                    return true;
                }
                if (
                    sch.ParentOrganizationResponse !=
                    lastSave.ParentOrganizationResponse
                ) {
                    return true;
                }
                if (
                    sch.ParentDepartmentResponse !=
                    lastSave.ParentDepartmentResponse
                ) {
                    return true;
                }
                if (
                    sch.ScholarshipEssayResponse !=
                    lastSave.ScholarshipEssayResponse
                ) {
                    return true;
                }
                if (
                    sch.ScholarshipEssay2Response !=
                    lastSave.ScholarshipEssay2Response
                ) {
                    return true;
                }
            }
        }

        return false;
    }

    postSelections(dto: AppSelection) {
        // test for any scholarships selected
        let selectedScholarshipTest = dto.Scholarships.filter(
            (a) => a.Selected == true
        );

        //display snack error is none are selected to be saved.
        if (selectedScholarshipTest.length == 0) {
            this.snackBarSvc.error(
                'Please select at least one scholarship'
            );
            return;
        }

        let formEdited = this.selectionsEdited(dto.Scholarships);

        if (formEdited === false) {
            this.snackBarSvc.error(
                'Form has not been edited since last save',
                5000
            );
            return;
        }

        this.appSvc
            .SaveAppSelection(dto)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    this.savedSelections = dto.Scholarships;
                    this.snackBarSvc.success('Save Successful', 3000);

                    this.currentPage = 7;

                    if (this.highestPage < 7) {
                        this.highestPage = 7;
                    }

                    // Update the selections portion of the initial application, for change tracking.
                    this.updateInitialForChangeTracking(6);

                    this.authSvc.resetIdleTimer();
                },
                error: (error) => {
                    this.errorSvc.HandleServiceError(error);
                }
            });
    }

    submitSignature(res: AppSignature) {
        if (res == null || res.Signature == null) {
            return;
        }

        if (res.Type !== 'STUDENT' && res.Type !== 'PARENT') {
            return;
        }

        let dto: AppSignature = {
            AppID: this.application.AppID,
            Type: res.Type,
            Signature: res.Signature
        };

        this.appSvc
            .SaveAppSignature(dto)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (result) => {
                    if (res.Type === 'STUDENT') {
                        this.studentSignature.set(res.Signature);
                    }
                    else if (res.Type === 'PARENT') {
                        this.parentSignature.set(res.Signature);
                    }

                    this.snackBarSvc.success('Save Successful', 3000);
                    this.authSvc.resetIdleTimer();
                },
                error: (error) => {
                    this.errorSvc.HandleServiceError(error);
                }
            });
    }

    resetRadio(value: boolean, scholarshipIndex: number) {
        // If the clicked value is already selected, clear it. Otherwise, set it.
        if (this.scholarshipArray.at(scholarshipIndex).get('AdditionalQuestionYesNoResponse')?.value === value) {
            this.scholarshipArray.at(scholarshipIndex).get('AdditionalQuestionYesNoResponse').reset();
        } else {
            this.scholarshipArray.at(scholarshipIndex).get('AdditionalQuestionYesNoResponse').setValue(value);
        }
    }

    anyUnselectedResponses(scholarships: ScholarshipResponses[]): string[] {
        let unselectedResponses: string[] = [];

        for (let sel of scholarships) {
            if (sel.Selected !== true) {
                if (
                    sel.AqHaveText === true &&
                    sel.AdditionalQuestionTextResponse != null &&
                    sel.AdditionalQuestionTextResponse.trim() != ''
                ) {
                    unselectedResponses.push(sel.ScholarshipName);
                    continue;
                }
                if (
                    sel.AqHaveYesNo === true &&
                    sel.AdditionalQuestionYesNoResponse != null
                ) {
                    unselectedResponses.push(sel.ScholarshipName);
                    continue;
                }
                if (
                    sel.EmployeeNameResponse != null &&
                    sel.EmployeeNameResponse != ''
                ) {
                    unselectedResponses.push(sel.ScholarshipName);
                    continue;
                }
                if (
                    sel.EmployeeLocationResponse != null &&
                    sel.EmployeeLocationResponse != ''
                ) {
                    unselectedResponses.push(sel.ScholarshipName);
                    continue;
                }
                if (
                    sel.EmployeeSSNResponse != null &&
                    sel.EmployeeSSNResponse != ''
                ) {
                    unselectedResponses.push(sel.ScholarshipName);
                    continue;
                }
                if (
                    sel.StoreLocationNumberResponse != null &&
                    sel.StoreLocationNumberResponse != ''
                ) {
                    unselectedResponses.push(sel.ScholarshipName);
                    continue;
                }
                if (
                    sel.AccountNameResponse != null &&
                    sel.AccountNameResponse != ''
                ) {
                    unselectedResponses.push(sel.ScholarshipName);
                    continue;
                }
                if (
                    sel.AccountNumberResponse != null &&
                    sel.AccountNumberResponse != ''
                ) {
                    unselectedResponses.push(sel.ScholarshipName);
                    continue;
                }
                if (
                    sel.ParentNameResponse != null &&
                    sel.ParentNameResponse != ''
                ) {
                    unselectedResponses.push(sel.ScholarshipName);
                    continue;
                }
                if (
                    sel.ParentSSNResponse != null &&
                    sel.ParentSSNResponse != ''
                ) {
                    unselectedResponses.push(sel.ScholarshipName);
                    continue;
                }
                if (
                    sel.ParentOrganizationResponse != null &&
                    sel.ParentOrganizationResponse != ''
                ) {
                    unselectedResponses.push(sel.ScholarshipName);
                    continue;
                }
                if (
                    sel.ParentDepartmentResponse != null &&
                    sel.ParentDepartmentResponse != ''
                ) {
                    unselectedResponses.push(sel.ScholarshipName);
                    continue;
                }
                if (
                    sel.ScholarshipEssayResponse != null &&
                    sel.ScholarshipEssayResponse != ''
                ) {
                    unselectedResponses.push(sel.ScholarshipName);
                    continue;
                }
                if (
                    sel.ScholarshipEssay2Response != null &&
                    sel.ScholarshipEssay2Response != ''
                ) {
                    unselectedResponses.push(sel.ScholarshipName);
                    continue;
                }
            }
        }

        return unselectedResponses;
    }

    postApplication() {
        let dialog = this.optionDlgSvc.Open({
            disableClose: true,
            TitleText: 'Are You Sure?',
            MessageText:
                `Have you filled out all pages of this application completely and selected all the scholarships you want to apply for? ` +
                `<br><br>After you submit, you will not be able to go back and make any changes.`,
            Options: [
                {
                    DisplayText: `Cancel - Go Back To Application`,
                    Color: 'primary',
                    Response: null,
                },
                {
                    DisplayText: 'Finalize Application',
                    Color: 'warn',
                    Response: 'submit',
                },
            ],
        });

        dialog
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((response) => {
                if (response === 'submit') {
                    let dto: AppFinalize = {
                        AppID: this.application.AppID,
                    };

                    this.appSvc
                        .FinalizeApp(dto)
                        .pipe(takeUntilDestroyed(this.destroyRef))
                        .subscribe({
                            next: (res) => {
                                this.snackBarSvc.success(
                                    'Application Submitted',
                                    8000
                                );

                                // Disable change tracking so we don't get caught on the route out.
                                this.takeDownTracking();

                                this.router.navigate([
                                    'summary',
                                    this.application.AppID,
                                ]);
                            },
                            error: (error) => {
                                this.snackBarSvc.error(
                                    error.error,
                                    10000
                                );
                            }
                        });
                }
            });
    }

    updateInitialForChangeTracking(section?: number) {
        let initCopy = JSON.parse(this.initialApplication);

        switch (section) {
            case 1: // Student info
                initCopy.StudetnForm = this.studentForm.getRawValue();
                break;
            case 2: // Financial info
                initCopy.FinancialForm = this.financialForm.getRawValue();
                break;
            case 3: // Family info
                initCopy.FamilyForm = this.familyForm.getRawValue();
                break;
            case 4: // College info
                initCopy.CollegeForm = this.collegeForm.getRawValue();
                break;
            case 5: // Essay info
                initCopy.EssayForm = this.essayForm.getRawValue();
                break;
            case 6: // Selections
                initCopy.SelectionForm = this.selectionForm.getRawValue();
                break;
            default: // Entire setup.
                this.initialApplication = this.getApplicationCompareJSON();
                return;
        }

        this.initialApplication = JSON.stringify(initCopy);
    }
}
